import { addNoteResponseSchema, conversationPeopleResponseSchema, conversationsPageSchema, type ConversationsPage, type ConversationsQuery } from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { leadKeys } from "../leads/lead-queries";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { refreshWhileProcessing } from "../telephony/call-display";
import { conversationKeys } from "./conversation-keys";

// Le letture e gli appunti di «Conversazioni» (piano M4, Task 19; firme del Task 18): `GET /api/conversations/calls` a pagine con
// il cursore, con il periodo, i filtri e la ricerca dell'indirizzo; la prima pagina di partenza la legge in anticipo la voce della
// rail, come le altre voci, così la pagina si apre subito. Mentre una telefonata mostrata ha la registrazione in preparazione la
// pagina si rilegge da sola (`refreshWhileProcessing`). Un appunto è `POST /api/leads/:leadId/notes` con la telefonata: poi la
// pagina si rilegge (gli appunti stanno nella riga) e così la Timeline di quel Lead.

/** Le righe di una pagina (il server ne accetta da 1 a 100, di partenza 50). */
export const CONVERSATIONS_PAGE_SIZE = 50;

/** Che cosa chiedere al server, senza cursore né limite: la lettura delle pagine li aggiunge. */
export type ConversationsRequest = Omit<ConversationsQuery, "cursor" | "limit">;

/** La vista di partenza dell'indirizzo `/conversazioni` senza parametri: la «Settimana», tutte le telefonate che si vedono. */
export const DEFAULT_CONVERSATIONS_QUERY: ConversationsRequest = { period: "last7" };

function callsPath(query: ConversationsRequest, cursor: string | null): string {
  const params = new URLSearchParams({ period: query.period });
  if (query.from !== undefined) params.set("from", query.from);
  if (query.to !== undefined) params.set("to", query.to);
  if (query.who !== undefined) params.set("who", query.who);
  if (query.direction !== undefined) params.set("direction", query.direction);
  if (query.outcome !== undefined) params.set("outcome", query.outcome);
  if (query.ownerAtCall !== undefined) params.set("ownerAtCall", query.ownerAtCall);
  if (query.recording !== undefined) params.set("recording", query.recording);
  if (query.audioOnly !== undefined) params.set("audioOnly", String(query.audioOnly));
  if (query.q !== undefined && query.q !== "") params.set("q", query.q);
  if (cursor !== null) params.set("cursor", cursor);
  params.set("limit", String(CONVERSATIONS_PAGE_SIZE));
  return `/api/conversations/calls?${params.toString()}`;
}

/** Le pagine delle telefonate: la successiva dal cursore della precedente, finché il server ne dà uno. */
export function conversationsPagesQuery(tab: TabContext, query: ConversationsRequest, view: string | null) {
  return infiniteQueryOptions({
    queryKey: conversationKeys.calls(tabKey(tab), view, query),
    queryFn: ({ signal, pageParam }) => apiRequest("GET", callsPath(query, pageParam), { schema: conversationsPageSchema, tab, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ConversationsPage) => last.nextCursor ?? undefined,
    // Tornare alla pagina entro mezzo minuto la mostra subito; una chiamata finita o un appunto la fanno rileggere da sé.
    staleTime: 30_000,
    // Una registrazione in preparazione arriva qualche minuto dopo la chiamata: la pagina si rilegge finché ce n'è una.
    refetchInterval: (query) => refreshWhileProcessing(query.state.data?.pages.flatMap((page) => page.rows.map((row) => (row.fromClose ? null : row.recording?.state))) ?? []),
  });
}

/**
 * Le persone di «Di chi» e «Owner allora», nelle telefonate e nei WhatsApp: tutte quelle dell'azienda, anche chi non può avere Lead
 * (un Admin che chiama), sospeso o archiviato, perché le sue conversazioni restano (`GET /api/conversations/people`).
 */
export function conversationPeopleQuery(tab: TabContext) {
  return queryOptions({
    queryKey: [...conversationKeys.all(tabKey(tab)), "people"] as const,
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/conversations/people", { schema: conversationPeopleResponseSchema, tab, signal })).people,
    staleTime: 60_000,
  });
}

/**
 * Legge in anticipo la prima pagina di partenza (velocità: criterio di accettazione), per la voce della rail: la chiave è quella
 * della pagina aperta senza parametri, nella «Guarda come…» della scheda se c'è. Non rilegge una pagina ancora fresca.
 */
export function prefetchConversations(queryClient: QueryClient, tab: TabContext): Promise<void> {
  return queryClient.prefetchInfiniteQuery(conversationsPagesQuery(tab, DEFAULT_CONVERSATIONS_QUERY, commercialViewOf(tab)));
}

/**
 * «Salva l'appunto»: un appunto sulla telefonata, che vuole la lettura e la modifica del Lead (il server risponde `404` o `403`
 * altrimenti). Salvato, la pagina si rilegge (l'appunto nuovo sta nella riga) e così le Timeline di quel Lead, che lo mostrano
 * sotto la stessa telefonata.
 */
export function useAddCallNote(tab: TabContext, view: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, callId, body }: { leadId: string; callId: string; body: string }) =>
      apiRequest("POST", `/api/leads/${encodeURIComponent(leadId)}/notes`, { schema: addNoteResponseSchema, body: { body, callId }, tab }),
    onSuccess: async (_response, { leadId }) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabKey(tab), leadId, view) });
      await queryClient.invalidateQueries({ queryKey: conversationKeys.all(tabKey(tab)) });
    },
  });
}
